import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { DatePicker } from '@mui/lab';
import moment from 'moment';
import format from 'string-template';

import User from '../../../../../Model/User';
import UserNutritionLog from '../../../../Model/UserNutritionLog';
import UserNutritionMacroGoals from '../../../../Model/UserNutritionMacroGoals';
import { useClientMealPlans, withMealPlanContextReady } from '../../../../context/MealPlanContext';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import LoadingPage from '../../../../../components/LoadingPage';
import { DateFormat } from '../../../../../utils/date';
import NutritionalStats from './NutritionalStats';
import FoodSummary from './FoodSummary';

import {
  Container,
  DateFilterWrapper,
  FilterWrapper,
  StyledFormHelperText,
  FilterInformation,
  TitleRow,
  FilterTitle,
  ProductSelect,
  FilterDescription,
  FilterDates,
  StyledTextField,
} from './styles';
import texts from './texts.json';

// TODO: Update with date range period selector(component).
const FromDateDifference = -7;

const FoodLog = ({ userDoc }) => {
  const clientAssignments = useClientMealPlans(userDoc?.id);

  const [userNutritionLogsCollection, setUserNutritionLogsCollection] = useState();
  const [fromDate, setFromDate] = useState(moment().add(FromDateDifference, 'days'));
  const [toDate, setToDate] = useState(moment());
  const [macroGoalsDoc, setMacroGoalsDoc] = useState({});
  const [selectedProductId, setSelectedProductId] = useState('');
  const [isReady, setIsReady] = useState(false);
  const isComponentMountedRef = useComponentMounted();

  const hasOverwrittenMacroGoals = !!macroGoalsDoc?.macroAverages?.macros
    && !!macroGoalsDoc?.macroAverages?.percentages;
  const isProductSelectorVisible = clientAssignments.length > 1 && !hasOverwrittenMacroGoals;

  const productOptions = useMemo(() => clientAssignments.map(({ productId, productName }) => ({
    value: productId,
    label: productName || texts.unnamedProduct,
  })), [clientAssignments]);

  const dateRangeText = format(texts.nutritionStatsDescription, {
    fromDate: fromDate.format(DateFormat.DATE_MONTH_SHORT_DISPLAY_FORMAT),
    toDate: toDate.format(DateFormat.DATE_MONTH_SHORT_DISPLAY_FORMAT),
  });

  const filteredLogs = useMemo(() => {
    if (userNutritionLogsCollection) {
      return userNutritionLogsCollection.docs.filter(({ startTime }) => (
        moment(startTime.toDate()).isBetween(fromDate, toDate, 'day', '[]')
      ));
    }
    return [];
  }, [
    fromDate,
    toDate,
    userNutritionLogsCollection,
  ]);

  useEffect(() => {
    const init = async () => {
      const {
        id: userId,
      } = userDoc;
      // Read here rather than in NutritionalStats: this component needs it for the selector.
      const [nutritionLogsCollection, userMacroGoals] = await Promise.all([
        UserNutritionLog.getUserNutritionLogs(userId),
        UserNutritionMacroGoals.getById(userId),
      ]);

      if (isComponentMountedRef.current) {
        setUserNutritionLogsCollection(nutritionLogsCollection);
        setMacroGoalsDoc(userMacroGoals || {});
        setIsReady(true);
      }
    };
    if (!isReady) {
      init();
    }
  }, [
    userDoc,
    isReady,
    isComponentMountedRef,
  ]);

  /*
    Default to a product that actually has a plan. Rows without one sort first, so taking the first
    row would open on a pending product and render 0g goals next to the client's real intake.
  */
  useEffect(() => {
    if (!selectedProductId && clientAssignments.length) {
      const withPlan = clientAssignments.find((assignment) => !!assignment.assignmentId);
      setSelectedProductId((withPlan || clientAssignments[0]).productId || '');
    }
  }, [
    clientAssignments,
    selectedProductId,
  ]);

  if (!isReady) {
    return <LoadingPage />;
  }

  return (
    <Container>
      <FilterWrapper>
        <FilterInformation>
          <TitleRow>
            <FilterTitle>
              {texts.nutritionStats}
            </FilterTitle>
            {isProductSelectorVisible && (
              <ProductSelect
                value={selectedProductId}
                options={productOptions}
                onChange={(event) => setSelectedProductId(event.target.value)}
              />
            )}
          </TitleRow>
          <FilterDescription>
            {dateRangeText}
          </FilterDescription>
        </FilterInformation>
        <FilterDates>
          <DateFilterWrapper>
            <DatePicker
              value={fromDate}
              inputFormat={DateFormat.DATE_YEAR_MONTH_SHORT_DISPLAY_FORMAT}
              onChange={(newFromDate) => setFromDate(newFromDate)}
              maxDate={toDate}
              renderInput={(params) => (
                <>
                  <StyledFormHelperText>{texts.date.from}</StyledFormHelperText>
                  <StyledTextField {...params} onKeyDown={(e) => e.preventDefault()} variant="outlined" />
                </>
              )}
            />
          </DateFilterWrapper>
          <DateFilterWrapper>
            <DatePicker
              value={toDate}
              inputFormat={DateFormat.DATE_YEAR_MONTH_SHORT_DISPLAY_FORMAT}
              onChange={(newToDate) => setToDate(newToDate)}
              minDate={fromDate}
              renderInput={(params) => (
                <>
                  <StyledFormHelperText>{texts.date.to}</StyledFormHelperText>
                  <StyledTextField {...params} onKeyDown={(e) => e.preventDefault()} variant="outlined" />
                </>
              )}
            />
          </DateFilterWrapper>
        </FilterDates>
      </FilterWrapper>
      <NutritionalStats
        user={userDoc.id}
        nutritionLogs={filteredLogs}
        macroGoalsDoc={macroGoalsDoc}
        selectedProductId={selectedProductId}
      />
      <FoodSummary
        nutritionLogs={filteredLogs}
        dateRange={dateRangeText}
      />
    </Container>
  );
};

FoodLog.propTypes = {
  userDoc: PropTypes.instanceOf(User),
};

FoodLog.defaultProps = {
  userDoc: null,
};

/*
  Gated on the meal plan context, not only `observer`: the default-selection effect runs after
  the first render, so an ungated FoodLog paints a frame with `selectedProductId` still empty.
*/
export default compose(
  withMealPlanContextReady,
  observer,
)(FoodLog);
